import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    ChangeSetItem,
    changeSetItemFactory as cif, EntityCacheDispatcher,
    EntityCollectionService,
    EntityServices
} from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import {
    debounceTime,
    filter,
    map,
    share,
    startWith,
    take,
    takeWhile,
    tap
} from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AccountContract } from '../../../account/store';
import * as fromDataServices from '../../../data-services';
import { InvoiceSetup } from '../../../data-services/invoice/setup/invoice';
import { InvoiceSessionSetup } from '../../../data-services/invoice/setup/session';
import * as fromLookup from '../../../lookups';
import * as fromShared from '../../../shared';
import { Process, ResponseStatus, SelectItem } from '../../../shared';
import { LoggingService } from '../../../shared/services/logging';
export enum InvoicePropertyType {
    AccountClass = 1,
    SignatureLine = 2,
    Disclaimer = 3,
    PaymentTerms = 4
}
@Component({
    selector: 'ef-invoice-setup-container',
    templateUrl: './invoice-setup-container.component.html',
    styleUrls: ['./invoice-setup-container.component.scss'],
    standalone: false
})
export class InvoiceSetupContainerComponent implements OnInit, OnDestroy {
    invoiceSessionSetupDS: EntityCollectionService<fromDataServices.Invoice.Setup.Session.InvoiceSessionSetup>;
    invoiceDetailOptionsDS: EntityCollectionService<fromDataServices.Invoice.Setup.InvoiceDetailOption.InvoiceDetailOptionsModel>;
    invoiceAddressSetupDS: EntityCollectionService<fromDataServices.Invoice.Setup.Address.InvoiceAddressSetup>;
    invoiceSelectionSetupDS: EntityCollectionService<fromDataServices.Invoice.Setup.Selections.InvoiceSelections>;
    invoiceSetupDS: EntityCollectionService<fromDataServices.Invoice.Setup.Invoice.InvoiceSetup>;
    addressDS: EntityCollectionService<fromDataServices.Address.Address>;
    addressTypeDS: EntityCollectionService<fromDataServices.Invoice.Setup.AddressType.InvoiceAddressType>;
    invoicePropertyDS: EntityCollectionService<fromDataServices.Invoice.Setup.InvoiceProperty.InvoiceProperty>;
    states$: Observable<SelectItem[]>;
    process$: Observable<Process>;
    addressTypes$: Observable<SelectItem[]>;
    invoiceProperties$: Observable<SelectItem[]>;
    accountClassProperties$: Observable<SelectItem[]>;
    signatureLineProperties$: Observable<SelectItem[]>;
    invoiceViewOptions$: Observable<SelectItem[]>;
    disclaimerProperties$: Observable<SelectItem[]>;
    paymentTermsProperties$: Observable<SelectItem[]>;
    chargeNumberSelectItems$: Observable<SelectItem[]>;
    invoiceTypeSelectItems$: Observable<SelectItem[]>;
    invoices$: Observable<
        fromDataServices.Invoice.Setup.Invoice.InvoiceSetup[]
    >;
    filteredInvoices$: Observable<
        fromDataServices.Invoice.Setup.Invoice.InvoiceSetup[]
    >;
    invoiceContractSelectItems$: Observable<SelectItem[]>;
    sessions$: Observable<
        fromDataServices.Invoice.Setup.Session.InvoiceSessionSetup[]
    >;
    filteredSessions$: Observable<
        fromDataServices.Invoice.Setup.Session.InvoiceSessionSetup[]
    >;
    invoiceSessionSelectItems$: Observable<SelectItem[]>;
    addressSetup$: Observable<fromDataServices.Address.Address[]>;
    addressSelectItems$: Observable<SelectItem[]>;
    remitAddressSelectItems$: Observable<SelectItem[]>;
    billingAddressSelectItems$: Observable<SelectItem[]>;
    selections$: Observable<
        fromDataServices.Invoice.Setup.Selections.InvoiceSelections[]
    >;
    selectionSelectItems$: Observable<SelectItem[]>;
    SelectContractsSubject = new ReplaySubject<AccountContract[]>(1);
    private active = true;
    constructor(
        private store: Store,
        private log: LoggingService,
        public entityServices: EntityServices,
        public dispatcher: EntityCacheDispatcher
    ) {
        this.log.verbose({ type: 'InvoiceSetupContainerComponent::' });
        this.invoiceSessionSetupDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.Setup.Session.InvoiceSessionSetup>(
                fromDataServices.Invoice.Setup.Session.entityName
            );
        this.invoiceAddressSetupDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.Setup.Address.InvoiceAddressSetup>(
                fromDataServices.Invoice.Setup.Address.entityName
            );
        this.invoiceSelectionSetupDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.Setup.Selections.InvoiceSelections>(
                fromDataServices.Invoice.Setup.Selections.entityName
            );
        this.invoiceSetupDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.Setup.Invoice.InvoiceSetup>(
                fromDataServices.Invoice.Setup.Invoice.entityName
            );
        this.addressDS =
            entityServices.getEntityCollectionService<fromDataServices.Address.Address>(
                fromDataServices.Address.entityName
            );
        this.addressTypeDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.Setup.AddressType.InvoiceAddressType>(
                fromDataServices.Invoice.Setup.AddressType.entityName
            );
        this.invoicePropertyDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.Setup.InvoiceProperty.InvoiceProperty>(
                fromDataServices.Invoice.Setup.InvoiceProperty.entityName
            );
        this.invoicePropertyDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.Setup.InvoiceProperty.InvoiceProperty>(
                fromDataServices.Invoice.Setup.InvoiceProperty.entityName
            );
        this.invoiceDetailOptionsDS =
            entityServices.getEntityCollectionService<fromDataServices.Invoice.Setup.InvoiceDetailOption.InvoiceDetailOptionsModel>(
                fromDataServices.Invoice.Setup.InvoiceDetailOption.entityName
            );
    }

    ngOnInit(): void {
        this.addressDS.clearCache();
        this.addressTypeDS.clearCache();
        this.invoicePropertyDS.clearCache();
        this.invoiceSetupDS.clearCache();
        this.invoiceSessionSetupDS.clearCache();
        this.invoiceAddressSetupDS.clearCache();
        this.invoiceSelectionSetupDS.clearCache();
        this.invoiceDetailOptionsDS.clearCache();

        this.addressDS.getAll().pipe(
            tap(addresses =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent.addressDS.getAll',
                    addresses
                })
            )
        );
        this.addressTypeDS.getAll();
        this.invoicePropertyDS.getAll();
        this.invoiceAddressSetupDS.getAll();
        this.invoiceSelectionSetupDS.getAll();
        this.invoiceDetailOptionsDS.getAll();
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.addressTypes$ = this.addressTypeDS.entities$.pipe(
            filter(items => !!items),
            map(
                items =>
                    clone(
                        items
                    ) as fromDataServices.Invoice.Setup.AddressType.InvoiceAddressType[]
            ),
            map(items =>
                items.map(item => ({
                    label: item.Addressdesc,
                    value: item.id
                }))
            ),
            tap(items =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:addressTypes$',
                    items
                })
            )
        );
        this.invoiceProperties$ = this.invoicePropertyDS.entities$.pipe(
            filter(items => !!items),
            map(items => clone(items)),
            map(items =>
                items.map(item => ({
                    label: item.Propertyname,
                    value: item.id
                }))
            )
        );
        this.invoices$ = this.invoiceSetupDS.entities$.pipe(
            map(items => clone(items))
        );
        this.chargeNumberSelectItems$ = this.invoices$.pipe(
            map(items => fromShared.uniqBy(items, x => x.Chargenumber)),
            map(items => clone(items)),
            map(items =>
                items
                    .filter(item => item.Chargenumber)
                    .sort((a, b) =>
                        a.Chargenumber.localeCompare(b.Chargenumber)
                    )
            ),
            map(items =>
                items
                    .filter(item => item.Chargenumber)
                    .map(item => ({
                        label: item.Chargenumber,
                        value: item.Chargenumber
                    }))
            )
        );
        this.invoiceTypeSelectItems$ = this.invoices$.pipe(
            map(items => fromShared.uniqBy(items, x => x.Contracttype)),
            map(items => clone(items)),
            map(items =>
                items.sort((a, b) =>
                    a.Contracttype.localeCompare(b.Contracttype)
                )
            ),
            map(items =>
                items.map(item => ({
                    label: item.Contracttype,
                    value: item.Contracttype
                }))
            )
        );
        this.invoiceContractSelectItems$ = this.invoices$.pipe(
            map(items => fromShared.uniqBy(items, x => x.Contractdesc)),
            map(items =>
                items.map(item => ({
                    label: item.Contractdesc,
                    value: item.Contractdesc
                }))
            )
        );
        this.sessions$ = this.invoiceSessionSetupDS.entities$.pipe(
            map(items => clone(items))
        );
        this.invoiceSessionSelectItems$ = this.sessions$.pipe(
            map(items => fromShared.uniqBy(items, x => x.Contractdesc)),
            map(items =>
                items.map(item => ({
                    label: item.Contractdesc,
                    value: item.Contractdesc
                }))
            )
        );
        this.addressSetup$ = this.addressDS.entities$.pipe(
            share(),
            map(items => [{ id: -1, Addressid: -1 } as any, ...clone(items)])
        );
        this.addressSelectItems$ = this.addressDS.entities$.pipe(
            map(items => items.map(item => this.getAddressSelectItem(item)))
        );
        this.remitAddressSelectItems$ = this.addressDS.entities$.pipe(
            tap(addresses =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent.remitAddressSelectItems$',
                    addresses
                })
            ),
            map(items =>
                items.filter(
                    item =>
                        item.AddressType &&
                        item.AddressType.toLowerCase() === 'remit'
                )
            ),
            tap(addresses =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent.remitAddressSelectItems$:filtered',
                    addresses
                })
            ),
            map(items => items.map(item => this.getAddressSelectItem(item))),
            tap(addresses =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent.remitAddressSelectItems$:mapped',
                    addresses
                })
            )
        );
        this.billingAddressSelectItems$ = this.addressDS.entities$.pipe(
            map(items =>
                items.filter(
                    item =>
                        item.AddressType &&
                        item.AddressType.toLowerCase() === 'billing'
                )
            ),
            map(items => items.map(item => this.getAddressSelectItem(item)))
        );
        this.selections$ = this.invoiceSelectionSetupDS.entities$.pipe(
            map(items => [
                {
                    id: 'add',
                    Status: { StatusCode: ResponseStatus.Adding, Message: '' }
                } as fromDataServices.Invoice.Setup.Selections.InvoiceSelections,
                ...(clone(
                    items
                ) as fromDataServices.Invoice.Setup.Selections.InvoiceSelections[])
            ]),
            map(items =>
                // sort by status code, then by property type
                items.sort((a, b) =>
                    a.Status.StatusCode === b.Status.StatusCode
                        ? a.Invoicepropertyid - b.Invoicepropertyid
                        : b.Status.StatusCode - a.Status.StatusCode
                )
            )
        );
        this.selectionSelectItems$ = this.selections$.pipe(
            map(items =>
                items.map(item => ({
                    label: `${item.Propertylabel} - (${item.Propertytext})`,
                    value: item.Invoicepropertysharedid
                }))
            )
        );
        this.accountClassProperties$ = this.selections$.pipe(
            filter(items => !!items),
            map(items => clone(items)),
            map(items =>
                items
                    .filter(
                        item =>
                            item.Invoicepropertyid ===
                            InvoicePropertyType.AccountClass
                    )
                    .map(item => ({
                        label: `${item.Propertylabel} - (${item.Propertytext})`,
                        value: item.Invoicepropertysharedid
                    }))
            ),
            tap(items =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:accountClassProperties$',
                    items
                })
            )
        );
        this.signatureLineProperties$ = this.selections$.pipe(
            filter(items => !!items),
            map(items => clone(items)),
            map(items =>
                items
                    .filter(
                        item =>
                            item.Invoicepropertyid ===
                            InvoicePropertyType.SignatureLine
                    )
                    .map(item => ({
                        label: `${item.Propertylabel} - (${item.Propertytext})`,
                        value: item.Invoicepropertysharedid
                    }))
            ),
            tap(items =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:signatureLineProperties$',
                    items
                })
            )
        );
        this.invoiceViewOptions$ = this.invoiceDetailOptionsDS.entities$.pipe(
            filter(items => !!items),
            // map(items => clone(items)),
            map(items =>
                items.map(item => ({
                    label: item.Invoiceviewoption ? item.Invoiceviewoption : '',
                    value: item.Invoiceviewoptionid
                }))
            ),
            tap(items =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:invoiceViewOptions$',
                    items
                })
            )
        );
        this.disclaimerProperties$ = this.selections$.pipe(
            filter(items => !!items),
            map(items => clone(items)),
            map(items =>
                items
                    .filter(
                        item =>
                            item.Invoicepropertyid ===
                            InvoicePropertyType.Disclaimer
                    )
                    .map(item => ({
                        label: `${item.Propertylabel} - (${item.Propertytext})`,
                        value: item.Invoicepropertysharedid
                    }))
            ),
            tap(items =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:disclaimerProperties$',
                    items
                })
            )
        );
        this.paymentTermsProperties$ = this.selections$.pipe(
            filter(items => !!items),
            map(items => clone(items)),
            map(items =>
                items
                    .filter(
                        item =>
                            item.Invoicepropertyid ===
                            InvoicePropertyType.PaymentTerms
                    )
                    .map(item => ({
                        label: `${item.Propertylabel} - (${item.Propertytext})`,
                        value: item.Invoicepropertysharedid
                    }))
            ),
            tap(items =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:paymentTermsProperties$',
                    items
                })
            )
        );
        this.states$ = this.store.pipe(
            select(fromLookup.getStates),
            map(items =>
                items.map(item => ({ label: item.State, value: item.State }))
            )
        );
        this.SelectContractsSubject.pipe(
            takeWhile(() => this.active),
            filter(x => !!x && x.length > 0)
        ).subscribe(contracts => {
            this.invoiceSetupDS.clearCache();
            this.invoiceSetupDS.getWithQuery({
                ContractList: contracts.map(item => ({
                    ContractListID: item.Contractlistid,
                    ContractDesc: ''
                })) as any
            });
        });
        this.SelectContractsSubject.pipe(take(1)).subscribe(() => {
            this.invoiceSessionSetupDS.clearCache();
            this.invoiceSessionSetupDS.getAll();
        });
        this.filteredInvoices$ = combineLatest([
            this.SelectContractsSubject.pipe(
                startWith(null as AccountContract[])
            ),
            this.invoices$.pipe(debounceTime(500))
        ]).pipe(
            tap(([contracts, invoices]) =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:filteredInvoices$',
                    invoices,
                    contracts
                })
            ),
            map(([contracts, invoices]) =>
                contracts && contracts.length > 0
                    ? invoices.filter(i =>
                          contracts
                              .map(x => x.Contractlistid)
                              .includes(i.Contractlistid)
                      )
                    : []
            ),
            tap(invoices =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:filteredInvoices$',
                    invoices
                })
            )
        );
        this.filteredSessions$ = combineLatest([
            this.SelectContractsSubject.pipe(
                startWith(null as AccountContract[])
            ),
            this.sessions$
        ]).pipe(
            tap(([contracts, sessions]) =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:filteredInvoices$',
                    sessions,
                    contracts
                })
            ),
            map(([contracts, sessions]) =>
                contracts && contracts.length > 0
                    ? sessions.filter(i =>
                          contracts
                              .map(x => x.Contractlistid)
                              .includes(i.Contractlistid)
                      )
                    : []
            ),
            tap(sessions =>
                this.log.verbose({
                    type: 'InvoiceSetupContainerComponent:filteredInvoices$',
                    sessions
                })
            )
        );
    }
    getAddressSelectItem = (
        item: fromDataServices.Address.Address
    ): SelectItem => ({
        label: this.formatAddressLabel(item),
        value: item.Addressid
    });
    formatAddressLabel(item: fromDataServices.Address.Address): string {
        let label = !!(item.AddressName && item.AddressName.trim())
            ? `${item.AddressName} - (`
            : '';
        label += !!(item.Addr1 && item.Addr1.trim())
            ? `${item.Addr1.trim()}`
            : '';
        label += !!(item.Addr2 && item.Addr2.trim())
            ? `, ${item.Addr2.trim()}`
            : '';
        label += !!(item.Addr3 && item.Addr3.trim())
            ? `, ${item.Addr3.trim()}`
            : '';
        label += !!(item.Addr4 && item.Addr4.trim())
            ? `, ${item.Addr4.trim()}`
            : '';
        label += !!(item.City && item.City.trim())
            ? `, ${item.City.trim()}`
            : '';
        label += !!(item.State && item.State.trim())
            ? `, ${item.State.trim()}`
            : '';
        label += !!(item.PostalCode && item.PostalCode.trim())
            ? `, ${item.PostalCode.trim()}`
            : '';
        label += !!(item.AddressName && item.AddressName.trim()) ? ')' : '';
        return label;
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    onSaveAddress($event: fromDataServices.Address.Address[]) {
        this.addressDS.upsert($event[0]);
    }
    onDeleteAddress($event: fromDataServices.Address.Address) {
        this.addressDS.delete($event);
    }
    onSaveSelection(
        $event: fromDataServices.Invoice.Setup.Selections.InvoiceSelections[]
    ) {
        this.invoiceSelectionSetupDS.upsert($event[0]);
    }
    onDeleteSelection(
        $event: fromDataServices.Invoice.Setup.Selections.InvoiceSelections
    ) {
        this.invoiceSelectionSetupDS.delete($event);
    }
    onSaveInvoiceOptions(
        $event: fromDataServices.Invoice.Setup.Invoice.InvoiceSetup[]
    ) {
        this.invoiceSetupDS.upsert($event[0]);
    }
    onSaveSession(
        $event: fromDataServices.Invoice.Setup.Session.InvoiceSessionSetup[]
    ) {
        this.invoiceSessionSetupDS.upsert($event[0]);
    }
    onSelectContract($event: AccountContract[]) {
        this.log.verbose({
            type: 'InvoiceSetupContainerComponent:onSelectContract',
            $event
        });
        this.SelectContractsSubject.next($event);
    }
    onUpdateBulkInvoiceSetups($event: InvoiceSetup[]) {
        const changeSet: ChangeSetItem[] = [
            cif.update(
                fromDataServices.Invoice.Setup.Invoice.entityName,
                $event.map(item => ({ id: item.id as string, changes: item }))
            )
        ];
        this.log.verbose({
            type: 'InvoiceSetupContainerComponent:onUpdateBulkInvoiceSetups',
            $event,
            changeSet
        });
        this.dispatcher
            .saveEntities(
                changeSet,
                `${environment.apiLocation}/${fromDataServices.Invoice.Setup.Invoice.entityName}s`
            )
            .pipe(
                tap(x =>
                    this.log.verbose({
                        type: 'InvoiceSetupContainerComponent:onUpdateBulkInvoiceSetups:tap',
                        x
                    })
                ),
                map(changeset => changeset)
            )
            .subscribe(changeset => {
                // this.invoiceSessionSetupDS.updateManyInCache(
                //     changeset.changes.entries.
                // );
            });
        // this.ds.saveEntities(
        //     { changes: changeSet },
        //     `api/${fromDataServices.Invoice.Setup.Invoice.entityName}`
        // );
        // $event.forEach(invoice => {
        //     this.invoiceSetupDS.update(invoice);
        // });
    }
    onUpdateBulkVouchers($event: InvoiceSessionSetup[]) {
        this.log.verbose({
            type: 'InvoiceSetupContainerComponent:onUpdateBulkVouchers',
            $event
        });
        $event.forEach(invoice => {
            this.invoiceSessionSetupDS.update(invoice);
        });
    }
}
